class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.pubSubSource = 'cart-drawer-items';
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      const newHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (newHTML) sectionElement.innerHTML = newHTML;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (html == null) return null;
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();

    this.pubSubSource = 'cart-drawer-items';
  }

  onCartUpdate() {
    fetch(
      `/cart?sections=${this.getSectionsToRender()
        .map((section) => section.section)
        .join(',')}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const parsedSections = JSON.parse(responseText);

        this.getSectionsToRender().forEach((section) => {
          const container = document.getElementById(section.id);
          if (!container) return;
          const elementToReplace = container.querySelector(section.selector) || container;
          const newHTML = this.getSectionInnerHTML(parsedSections[section.section], section.selector);
          if (newHTML) elementToReplace.innerHTML = newHTML;
        });
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerTieredUpsell extends HTMLElement {
  connectedCallback() {
    requestAnimationFrame(this.setProgress.bind(this));
  }

  setProgress() {
    const cartDrawer = this.closest('cart-drawer');
    const progressBar = this.querySelector('.js-progress-bar');
    if (!(cartDrawer && progressBar)) {
      return;
    }
    let tierProgress = 0;

    const activeTier = this.querySelector('.js-tier.active');
    const nextTier = activeTier ? activeTier.previousElementSibling : this.querySelector('.js-tier:last-child');

    // Determine current and next tier from cart total
    const cartTotal = parseFloat(this.dataset.cartTotal);

    // Animate progress bar
    if (!nextTier) {
      // If in the final tier, just make the bar 100%
      tierProgress = 100;
    } else if (cartTotal === 0) {
      tierProgress = 0;
    } else {
      const nextThreshold = parseFloat(nextTier.dataset.tierThreshold);
      const currentThreshold = activeTier ? parseFloat(activeTier.dataset.tierThreshold) : 0;
      const range = nextThreshold - currentThreshold;
      const progress = (cartTotal - currentThreshold) / range;
      const tierStart = activeTier ? activeTier.offsetLeft + activeTier.offsetWidth / 2 : 0;
      const tierWidth = nextTier.offsetLeft + nextTier.offsetWidth / 2 - tierStart;
      // Don't let bar get *too* close to the next tier line
      const distanceWithin = Math.min(progress * tierWidth, tierWidth - 6);
      const fullWidth = progressBar.clientWidth;
      tierProgress = ((tierStart + distanceWithin) / fullWidth) * 100;
    }
    cartDrawer.style.setProperty('--cart-drawer-tiers-progress', tierProgress + '%');
  }
}

customElements.define('cart-drawer-tiered-upsell', CartDrawerTieredUpsell);
